import { apiFetch } from "@/lib/api";

export const HUB_NOTIFICATION_TOPICS = [
  "support_ticket",
  "purchase_request",
  "transfer_pack_adjustment",
  "inventory_discrepancy",
  "inventory_carton_adjustment",
  "document_request",
  "document_share",
  "shipping_paperwork",
  "shipping_inbound_arrival",
  "personal_package_arrival",
  "mrp_rfq",
  "mrp_rop_internal_build",
  "quality_containment",
  "quality_rma_receive_quantity_discrepancy",
  "clamp_qc_ready_for_credit",
  "ap_receipt_discrepancy",
  "ap_vendor_invoice",
  "ap_cbp_monthly_statement",
  "ap_early_payment",
  "gemini_document_extract_spend",
  "engineering_firmware_received",
  "engineering_firmware_approved",
  "ops_sidecar_down",
  "ops_database_stuck_session",
  "note_mention",
  "megaventory_qbo_housekeeping",
  "safety_report",
] as const;

export type HubNotificationTopic = (typeof HUB_NOTIFICATION_TOPICS)[number];

export const HUB_NOTIFICATION_TOPIC_LABELS: Record<HubNotificationTopic, string> = {
  support_ticket: "Support ticket",
  purchase_request: "Purchase request",
  transfer_pack_adjustment: "Transfer quantity adjustment",
  inventory_discrepancy: "Inventory discrepancy",
  inventory_carton_adjustment: "Inventory carton adjustment",
  document_request: "Document request",
  document_share: "Document share",
  shipping_paperwork: "Shipping paperwork",
  shipping_inbound_arrival: "Inbound dock arrival",
  personal_package_arrival: "Package arrival watch",
  mrp_rfq: "MRP RFQ",
  mrp_rop_internal_build: "Reorder point in-house build",
  quality_containment: "Quality containment",
  quality_rma_receive_quantity_discrepancy: "RMA receive quantity discrepancy",
  clamp_qc_ready_for_credit: "Clamp QC ready for credit",
  ap_receipt_discrepancy: "Receipt discrepancies",
  ap_vendor_invoice: "Vendor invoices",
  ap_cbp_monthly_statement: "CBP monthly statement",
  ap_early_payment: "Early payment",
  gemini_document_extract_spend: "GCP spend",
  engineering_firmware_received: "Engineering firmware received",
  engineering_firmware_approved: "Engineering firmware approved",
  ops_sidecar_down: "Hub sidecar outage",
  ops_database_stuck_session: "Stuck database session",
  note_mention: "Note mention",
  megaventory_qbo_housekeeping: "Megaventory USD to CNY and QuickBooks log",
  safety_report: "Safety report",
};

export const HUB_NOTIFICATION_TOPIC_ADMIN_MODULE: Record<HubNotificationTopic, string> = {
  support_ticket: "admin",
  purchase_request: "mrp",
  transfer_pack_adjustment: "manufacturing_management",
  inventory_discrepancy: "manufacturing_management",
  inventory_carton_adjustment: "warehouse",
  document_request: "documents",
  document_share: "documents",
  shipping_paperwork: "shipping",
  shipping_inbound_arrival: "shipping",
  personal_package_arrival: "personal",
  mrp_rfq: "mrp",
  mrp_rop_internal_build: "mrp",
  quality_containment: "quality",
  quality_rma_receive_quantity_discrepancy: "quality",
  clamp_qc_ready_for_credit: "clampqc",
  ap_receipt_discrepancy: "accounting",
  ap_vendor_invoice: "accounting",
  ap_cbp_monthly_statement: "accounting",
  ap_early_payment: "accounting",
  gemini_document_extract_spend: "admin",
  engineering_firmware_received: "engineering",
  engineering_firmware_approved: "engineering",
  ops_sidecar_down: "admin",
  ops_database_stuck_session: "admin",
  note_mention: "admin",
  megaventory_qbo_housekeeping: "admin",
  safety_report: "manufacturing_management",
};

export function isHubNotificationTopic(value: unknown): value is HubNotificationTopic {
  return typeof value === "string" && (HUB_NOTIFICATION_TOPICS as readonly string[]).includes(value);
}

export type NotificationListFilter = "all" | "unread" | "snoozed";

export const HUB_NOTIFICATIONS_FILTER_STORAGE_KEY = "hub.notifications.filter";

export function parseNotificationListFilter(value: unknown): NotificationListFilter | null {
  if (value === "all" || value === "unread" || value === "snoozed") {
    return value;
  }
  return null;
}

export function readStoredNotificationFilter(): NotificationListFilter | null {
  if (typeof localStorage === "undefined") {
    return null;
  }
  try {
    return parseNotificationListFilter(localStorage.getItem(HUB_NOTIFICATIONS_FILTER_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function storeNotificationFilter(filter: NotificationListFilter): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  try {
    localStorage.setItem(HUB_NOTIFICATIONS_FILTER_STORAGE_KEY, filter);
  } catch {
    /* localStorage can be unavailable in private windows */
  }
}

export type NotificationListRow = {
  eventId: string;
  topic: string;
  eventKind: string;
  sourceId: string;
  sourceStatus: string | null;
  title: string;
  summary: string | null;
  requiresAction: boolean;
  createdAt: string;
  readAt: string | null;
  snoozedUntil: string | null;
  destinationPath: string;
  ownerUserId: string | null;
  ownerDisplayName: string | null;
};

export type NotificationListCursor = {
  createdAt: string;
  eventId: string;
};

export type NotificationListResult = {
  rows: NotificationListRow[];
  nextCursor: NotificationListCursor | null;
};

export type NotificationUnreadCount = {
  unreadCount: number;
  /** Set by the live stream when these events should open the arrival window. */
  surfaceEventIds?: string[];
  arrivalToken?: number;
};

export type NotificationTopicOwner = {
  userId: string;
  email: string;
  displayName: string | null;
};

export type NotificationTopicOwnersResult = {
  topic: HubNotificationTopic;
  owners: NotificationTopicOwner[];
  backfill?: { eventsCreated?: number; deliveriesCreated?: number };
};

export type NotificationItemOwner = {
  ownerUserId: string;
  displayName: string | null;
  email: string;
};

export type NotificationListFilters = {
  filter: NotificationListFilter;
  topics?: HubNotificationTopic[];
};

type ApiNotificationListRow = {
  event_id: string;
  topic: string;
  event_kind: string;
  source_id: string;
  source_status: string | null;
  title: string;
  summary: string | null;
  requires_action: boolean;
  created_at: string;
  read_at: string | null;
  snoozed_until: string | null;
  destination_path: string;
  owner_user_id: string | null;
  owner_display_name: string | null;
};

function mapListRow(row: ApiNotificationListRow): NotificationListRow {
  return {
    eventId: row.event_id,
    topic: row.topic,
    eventKind: row.event_kind,
    sourceId: row.source_id,
    sourceStatus: row.source_status,
    title: row.title,
    summary: row.summary,
    requiresAction: row.requires_action,
    createdAt: row.created_at,
    readAt: row.read_at,
    snoozedUntil: row.snoozed_until,
    destinationPath: row.destination_path,
    ownerUserId: row.owner_user_id,
    ownerDisplayName: row.owner_display_name,
  };
}

export const notificationsUnreadCountQueryKey = ["notifications", "unread-count"] as const;

export function notificationsListQueryKey(filters: NotificationListFilters) {
  return [
    "notifications",
    "list",
    filters.filter,
    filters.topics != null && filters.topics.length > 0 ? [...filters.topics].sort().join(",") : "",
  ] as const;
}

export function notificationsTopicOwnersQueryKey(topic: HubNotificationTopic) {
  return ["notifications", "topic-owners", topic] as const;
}

export function notificationsItemOwnerQueryKey(topic: HubNotificationTopic, sourceId: string) {
  return ["notifications", "item-owner", topic, sourceId] as const;
}

export function markNotificationReadInListRow(
  row: NotificationListRow,
  eventId: string,
  readAt: string,
): NotificationListRow {
  if (row.eventId !== eventId) {
    return row;
  }
  return { ...row, readAt };
}

export function markNotificationReadInListRows(
  rows: NotificationListRow[],
  eventId: string,
  readAt: string,
): NotificationListRow[] {
  return rows.map((row) => markNotificationReadInListRow(row, eventId, readAt));
}

export function markNotificationUnreadInListRow(
  row: NotificationListRow,
  eventId: string,
): NotificationListRow {
  if (row.eventId !== eventId) {
    return row;
  }
  return { ...row, readAt: null };
}

export function markNotificationUnreadInListRows(
  rows: NotificationListRow[],
  eventId: string,
): NotificationListRow[] {
  return rows.map((row) => markNotificationUnreadInListRow(row, eventId));
}

export function snoozeNotificationInListRow(
  row: NotificationListRow,
  eventId: string,
  snoozedUntil: string,
): NotificationListRow {
  if (row.eventId !== eventId) {
    return row;
  }
  return { ...row, snoozedUntil };
}

export function snoozeNotificationInListRows(
  rows: NotificationListRow[],
  eventId: string,
  snoozedUntil: string,
): NotificationListRow[] {
  return rows.map((row) => snoozeNotificationInListRow(row, eventId, snoozedUntil));
}

export function removeNotificationFromListRows(
  rows: NotificationListRow[],
  eventId: string,
): NotificationListRow[] {
  return rows.filter((row) => row.eventId !== eventId);
}

export function removeNotificationFromListResult(
  result: NotificationListResult,
  eventId: string,
): NotificationListResult {
  return {
    ...result,
    rows: removeNotificationFromListRows(result.rows, eventId),
  };
}

export async function fetchNotificationsUnreadCount(): Promise<NotificationUnreadCount> {
  return apiFetch<NotificationUnreadCount>("/api/notifications/unread-count");
}

export async function fetchNotificationsList(input: {
  filter?: NotificationListFilter;
  topics?: HubNotificationTopic[];
  cursorCreatedAt?: string | null;
  cursorEventId?: string | null;
  limit?: number;
}): Promise<NotificationListResult> {
  const params = new URLSearchParams();
  params.set("filter", input.filter ?? "unread");
  if (input.topics != null && input.topics.length > 0) {
    params.set("topics", input.topics.join(","));
  }
  if (input.cursorCreatedAt != null && input.cursorEventId != null) {
    params.set("cursorCreatedAt", input.cursorCreatedAt);
    params.set("cursorEventId", input.cursorEventId);
  }
  if (input.limit != null) {
    params.set("limit", String(input.limit));
  }
  const result = await apiFetch<{
    rows: ApiNotificationListRow[];
    nextCursor: NotificationListCursor | null;
  }>(`/api/notifications?${params.toString()}`);
  return {
    rows: result.rows.map(mapListRow),
    nextCursor: result.nextCursor,
  };
}

export async function markNotificationRead(eventId: string): Promise<NotificationUnreadCount & { ok: true }> {
  return apiFetch<NotificationUnreadCount & { ok: true }>(
    `/api/notifications/${encodeURIComponent(eventId)}/read`,
    { method: "POST" },
  );
}

export async function markNotificationUnread(
  eventId: string,
): Promise<NotificationUnreadCount & { ok: true }> {
  return apiFetch<NotificationUnreadCount & { ok: true }>(
    `/api/notifications/${encodeURIComponent(eventId)}/unread`,
    { method: "POST" },
  );
}

export async function snoozeNotification(
  eventId: string,
  until: string,
): Promise<NotificationUnreadCount & { ok: true; snoozedUntil: string }> {
  return apiFetch<NotificationUnreadCount & { ok: true; snoozedUntil: string }>(
    `/api/notifications/${encodeURIComponent(eventId)}/snooze`,
    {
      method: "POST",
      body: { until },
    },
  );
}

export async function clearNotificationSnooze(
  eventId: string,
): Promise<NotificationUnreadCount & { ok: true }> {
  return apiFetch<NotificationUnreadCount & { ok: true }>(
    `/api/notifications/${encodeURIComponent(eventId)}/unsnooze`,
    { method: "POST" },
  );
}

export async function markAllNotificationsRead(
  topics?: HubNotificationTopic[],
): Promise<NotificationUnreadCount & { ok: true }> {
  return apiFetch<NotificationUnreadCount & { ok: true }>("/api/notifications/read-all", {
    method: "POST",
    body:
      topics != null && topics.length > 0
        ? { topics: topics.join(",") }
        : {},
  });
}

export async function fetchNotificationTopicOwners(
  topic: HubNotificationTopic,
): Promise<NotificationTopicOwnersResult> {
  return apiFetch<NotificationTopicOwnersResult>(
    `/api/notifications/topics/${encodeURIComponent(topic)}/owners`,
  );
}

export async function replaceNotificationTopicOwners(
  topic: HubNotificationTopic,
  userIds: string[],
): Promise<NotificationTopicOwnersResult> {
  return apiFetch<NotificationTopicOwnersResult>(
    `/api/notifications/topics/${encodeURIComponent(topic)}/owners`,
    {
      method: "PUT",
      body: { userIds },
    },
  );
}

export async function fetchNotificationItemOwner(
  topic: HubNotificationTopic,
  sourceId: string,
): Promise<{ owner: NotificationItemOwner | null }> {
  return apiFetch<{ owner: NotificationItemOwner | null }>(
    `/api/notifications/items/${encodeURIComponent(topic)}/${encodeURIComponent(sourceId)}/owner`,
  );
}

export async function claimNotificationItemOwner(
  topic: HubNotificationTopic,
  sourceId: string,
): Promise<{ ok: true; ownerUserId: string }> {
  return apiFetch<{ ok: true; ownerUserId: string }>(
    `/api/notifications/items/${encodeURIComponent(topic)}/${encodeURIComponent(sourceId)}/claim`,
    { method: "POST" },
  );
}

export async function assignNotificationItemOwner(
  topic: HubNotificationTopic,
  sourceId: string,
  ownerUserId: string,
): Promise<{ ok: true; ownerUserId: string }> {
  return apiFetch<{ ok: true; ownerUserId: string }>(
    `/api/notifications/items/${encodeURIComponent(topic)}/${encodeURIComponent(sourceId)}/owner`,
    {
      method: "PUT",
      body: { ownerUserId },
    },
  );
}

export async function releaseNotificationItemOwner(
  topic: HubNotificationTopic,
  sourceId: string,
): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>(
    `/api/notifications/items/${encodeURIComponent(topic)}/${encodeURIComponent(sourceId)}/owner`,
    { method: "DELETE" },
  );
}
